import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
  Modal,
  Pressable,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';

// Create portal for web tooltips
const createPortal = Platform.OS === 'web' ? require('react-dom').createPortal : null;

// Global tooltip state manager for smooth transitions
const tooltipManager = {
  activeTooltips: new Set<string>(),
  
  register(id: string) {
    this.activeTooltips.add(id);
  },
  
  unregister(id: string) {
    this.activeTooltips.delete(id);
  },
  
  hasActiveTooltips() {
    return this.activeTooltips.size > 0;
  },
  
  getActiveCount() {
    return this.activeTooltips.size;
  }
};

export type DLOTooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';
export type DLOTooltipTrigger = 'hover' | 'click' | 'manual';

export interface DLOTooltipProps {
  children: React.ReactNode;
  title?: string;
  content: string;
  customContent?: React.ReactNode;
  position?: DLOTooltipPosition;
  trigger?: DLOTooltipTrigger;
  visible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  maxWidth?: number;
  disabled?: boolean;
  style?: any;
  verticalOffset?: number; // Additional vertical offset for fine-tuning position
  horizontalOffset?: number; // Additional horizontal offset for fine-tuning position
  minWidth?: number; // Minimum width for tooltip
  padding?: number; // Custom padding for tooltip
  textAlign?: 'left' | 'center' | 'right'; // Text alignment for tooltip content
  fontSize?: number; // Font size for tooltip text
  titleFontSize?: number; // Font size for tooltip title
  contentHeightHint?: number; // Estimated content height, used to improve 'auto' flip decisions for tall custom content
}

// Arrow component for different positions
const TooltipArrow = ({ position, color = '#272727' }: { position: DLOTooltipPosition; color?: string }) => {
  const getArrowContainerStyle = () => {
    const baseStyle = { 
      position: 'absolute' as const,
      width: 8,
      height: 8,
    };
    
    switch (position) {
      case 'left':
        return { 
          ...baseStyle, 
          right: -4, 
          top: '50%' as any, 
          marginTop: -4,
          transform: [{ rotateZ: '45deg' }],
        };
      case 'right':
        return { 
          ...baseStyle, 
          left: -4, 
          top: '50%' as any, 
          marginTop: -4,
          transform: [{ rotateZ: '45deg' }],
        };
      case 'top':
        return { 
          ...baseStyle, 
          bottom: -4, 
          left: '50%' as any, 
          marginLeft: -4,
          transform: [{ rotateZ: '45deg' }],
        };
      case 'bottom':
        return { 
          ...baseStyle, 
          top: -4, 
          left: '50%' as any, 
          marginLeft: -4,
          transform: [{ rotateZ: '45deg' }],
        };
      default:
        return { 
          ...baseStyle, 
          right: -4, 
          top: '50%' as any, 
          marginTop: -4,
          transform: [{ rotateZ: '45deg' }],
        };
    }
  };

  return (
    <View style={getArrowContainerStyle()}>
      <View style={{
        width: 8,
        height: 8,
        backgroundColor: color,
      }} />
    </View>
  );
};

const DLOTooltip: React.FC<DLOTooltipProps> = ({
  children,
  title,
  content,
  customContent,
  position = 'top',
  trigger = 'hover',
  visible: controlledVisible,
  onVisibilityChange,
  maxWidth = 280,
  disabled = false,
  style,
  verticalOffset = 0,
  horizontalOffset = 0,
  minWidth,
  padding,
  textAlign = 'left',
  fontSize = 12,
  titleFontSize = 14,
  contentHeightHint,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [resolvedPosition, setResolvedPosition] = useState<DLOTooltipPosition>(
    position === 'auto' ? 'bottom' : position,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const triggerRef = useRef<View>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipContainerRef = useRef<HTMLElement | null>(null);
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).substr(2, 9)}`);

  // Native: measured tooltip dimensions from onLayout, used for placement
  const [nativeTooltipSize, setNativeTooltipSize] = useState({ width: 0, height: 0 });
  // Native: measured anchor position (set on press before showing)
  const nativeAnchorRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  // Native: which side the tooltip is actually rendered on after boundary check
  const [nativeActualPosition, setNativeActualPosition] = useState<DLOTooltipPosition>(position);
  
  const isControlled = controlledVisible !== undefined;
  const isVisible = isControlled ? controlledVisible : internalVisible;
  
  const screenData = Dimensions.get('window');
  const isMobile = screenData.width < 768;

  // Initialize tooltip container for web
  useEffect(() => {
    if (Platform.OS === 'web') {
      let container = document.getElementById('dlo-tooltip-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'dlo-tooltip-container';
        container.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 999999;
        `;
        document.body.appendChild(container);
      }
      tooltipContainerRef.current = container;
    }
  }, []);

  const updateVisibility = useCallback((newVisible: boolean) => {
    if (disabled) return;
    
    if (!isControlled) {
      setInternalVisible(newVisible);
    }
    onVisibilityChange?.(newVisible);
  }, [disabled, isControlled, onVisibilityChange]);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || Platform.OS !== 'web') return;

    const element = triggerRef.current as any;
    
    // Find the actual DOM node
    const findDOMNode = (node: any): HTMLElement | null => {
      if (!node) return null;
      if (node.nodeType === 1) return node;
      if (node._nativeTag?.nodeType === 1) return node._nativeTag;
      return null;
    };

    const domNode = findDOMNode(element);
    
    if (!domNode?.getBoundingClientRect) {
      console.warn('DLOTooltip: Could not find DOM node for positioning');
      return;
    }

    const rect = domNode.getBoundingClientRect();
    const tooltipWidth = Math.min(maxWidth, screenData.width - 32);
    
    // Better height estimation based on content
    const titleHeight = title ? 28 : 0; // Slightly increased for better spacing
    const dividerHeight = title ? 16 : 0; // Account for divider spacing
    const contentLines = customContent ? 3 : Math.ceil(content.length / 40); // Adjusted character count
    const contentHeight = contentHeightHint ?? contentLines * 16; // Line height (or caller-supplied hint for tall custom content)
    const paddingHeight = 32; // Top and bottom padding
    const tooltipHeight = paddingHeight + titleHeight + dividerHeight + contentHeight;
    
    let top = 0;
    let left = 0;
    const gap = 12; // Slightly larger gap for better visual separation

    // 'auto' flips between top/bottom based on how much viewport space is
    // available around the trigger (e.g. a header near the bottom of a
    // scrolled page gets the tooltip above it instead of clipped below).
    let effectivePosition: Exclude<DLOTooltipPosition, 'auto'> =
      position === 'auto' ? 'bottom' : position;
    if (position === 'auto') {
      const spaceBelow = screenData.height - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      effectivePosition = spaceBelow >= tooltipHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }
    setResolvedPosition(effectivePosition);

    switch (effectivePosition) {
      case 'top':
        top = rect.top - tooltipHeight - gap;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        // Perfect vertical centering for left position
        const triggerCenter = rect.top + (rect.height / 2);
        top = triggerCenter - (tooltipHeight / 2);
        left = rect.left - tooltipWidth - gap;
        break;
      case 'right':
        // Perfect vertical centering for right position  
        const triggerCenterRight = rect.top + (rect.height / 2);
        top = triggerCenterRight - (tooltipHeight / 2);
        left = rect.right + gap;
        break;
    }

    // Boundary checking with better margins
    const margin = 16;
    if (left < margin) left = margin;
    if (left + tooltipWidth > screenData.width - margin) {
      left = screenData.width - tooltipWidth - margin;
    }
    if (top < margin) top = margin;
    if (top + tooltipHeight > screenData.height - margin) {
      top = screenData.height - tooltipHeight - margin;
    }

    // Apply offsets for fine-tuning position
    top += verticalOffset;
    left += horizontalOffset;

    setTooltipPosition({ top, left });
  }, [position, maxWidth, screenData, title, content, customContent, contentHeightHint, verticalOffset, horizontalOffset]);

  // Show/hide handlers
  const handleShow = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsTransitioning(true);
    tooltipManager.register(tooltipId.current);
    updateVisibility(true);
    if (Platform.OS === 'web') {
      setTimeout(() => {
        calculatePosition();
        setIsTransitioning(false);
      }, 10);
    }
  }, [updateVisibility, calculatePosition]);

  const handleHide = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsTransitioning(true);
    tooltipManager.unregister(tooltipId.current);
    // Reduced delay for faster hide response
    setTimeout(() => {
      updateVisibility(false);
      setIsTransitioning(false);
    }, 30); // Reduced from 50ms to 30ms for snappier response
  }, [updateVisibility]);

  const handleMouseEnter = useCallback(() => {
    if (trigger === 'hover' && Platform.OS === 'web') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      
      // Check if any tooltips are currently active using our manager
      const hasActiveTooltips = tooltipManager.hasActiveTooltips();
      
      if (hasActiveTooltips || isTransitioning) {
        // If other tooltips are active or transitioning, show immediately for smooth transition
        handleShow();
      } else {
        // Normal delay for initial show when no other tooltips are active
        hoverTimeoutRef.current = setTimeout(handleShow, 200);
      }
    }
  }, [trigger, handleShow, isTransitioning]);

  const handleMouseLeave = useCallback(() => {
    if (trigger === 'hover' && Platform.OS === 'web') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Reduced delay from 150ms to 50ms for faster hide response
      hoverTimeoutRef.current = setTimeout(handleHide, 50);
    }
  }, [trigger, handleHide]);

  // Revert: handlePress uses updateVisibility directly (web path uses its own
  // portal with calculatePosition; native path measures anchor before showing)
  const handlePress = useCallback(() => {
    if (trigger === 'click') {
      updateVisibility(!isVisible);
    }
  }, [trigger, isVisible, updateVisibility]);

  // Native-only: measure the anchor and show the tooltip.
  // measureInWindow gives coords in screen space, independent of scroll/transforms.
  const handleNativePress = useCallback(() => {
    if (trigger !== 'click') return;

    if (isVisible) {
      updateVisibility(false);
      return;
    }

    if (triggerRef.current) {
      (triggerRef.current as any).measureInWindow(
        (x: number, y: number, width: number, height: number) => {
          nativeAnchorRef.current = { x, y, width, height };
          // Position will be finalised in onLayout once tooltip dimensions are known.
          // Show now so the Modal mounts and onLayout fires.
          updateVisibility(true);
        },
      );
    } else {
      updateVisibility(true);
    }
  }, [trigger, isVisible, updateVisibility]);

  // Cleanup
  useEffect(() => {
    // Global event listener to force hide tooltip when cursor leaves sidebar
    const handleSidebarLeave = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Force immediate hide without delay
      setIsTransitioning(false);
      tooltipManager.unregister(tooltipId.current);
      updateVisibility(false);
    };

    if (Platform.OS === 'web') {
      window.addEventListener('dlo-sidebar-leave', handleSidebarLeave);
    }

    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Clean up from tooltip manager
      tooltipManager.unregister(tooltipId.current);
      
      if (Platform.OS === 'web') {
        window.removeEventListener('dlo-sidebar-leave', handleSidebarLeave);
      }
    };
  }, [updateVisibility]);

  // Recalculate on visibility change
  useEffect(() => {
    if (isVisible && Platform.OS === 'web') {
      const timer = setTimeout(calculatePosition, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible, calculatePosition]);

  // Native: recompute tooltip top/left whenever the measured tooltip size changes
  // (onLayout fires after the tooltip View renders inside the Modal).
  const handleNativeTooltipLayout = useCallback(
    (event: { nativeEvent: { layout: { width: number; height: number } } }) => {
      const { width: tipW, height: tipH } = event.nativeEvent.layout;
      setNativeTooltipSize({ width: tipW, height: tipH });

      const { x: anchorX, y: anchorY, width: anchorW, height: anchorH } = nativeAnchorRef.current;
      const screen = Dimensions.get('window');
      const MARGIN = 12;
      const ARROW_SIZE = 10;
      const GAP = ARROW_SIZE + 4; // gap between arrow tip and anchor edge

      const tooltipW = Math.min(tipW, maxWidth, screen.width - MARGIN * 2);

      // Horizontal: centre on anchor, then clamp to screen
      let left = anchorX + anchorW / 2 - tooltipW / 2;
      left = Math.max(MARGIN, Math.min(left, screen.width - tooltipW - MARGIN));

      // Vertical: prefer above, fall back to below
      const spaceAbove = anchorY - GAP - MARGIN;
      const spaceBelow = screen.height - (anchorY + anchorH) - GAP - MARGIN;

      let top: number;
      let actualPos: DLOTooltipPosition;

      if (position === 'bottom' || (position === 'top' && spaceAbove < tipH && spaceBelow >= tipH)) {
        // Show below
        top = anchorY + anchorH + GAP;
        actualPos = 'bottom';
      } else {
        // Show above (default)
        top = anchorY - tipH - GAP;
        top = Math.max(MARGIN, top);
        actualPos = 'top';
      }

      setTooltipPosition({ top, left });
      setNativeActualPosition(actualPos);
    },
    [position, maxWidth],
  );

  const renderTooltipContent = () => (
    <View style={[
      styles.tooltip, 
      { 
        maxWidth, 
        ...(minWidth && { minWidth }),
        ...(padding && { padding })
      }, 
      style
    ]}>
      {title && (
        <>
          <Text style={[styles.tooltipTitle, { fontSize: titleFontSize }]} numberOfLines={2}>
            {title}
          </Text>
          <View style={styles.divider} />
        </>
      )}
      {customContent ? (
        customContent
      ) : (
        <Text style={[
          styles.tooltipText,
          { textAlign, fontSize }
        ]}>
          {content}
        </Text>
      )}
      {Platform.OS === 'web' && <TooltipArrow position={resolvedPosition} />}
    </View>
  );

  if (disabled) {
    return <>{children}</>;
  }

  // On web, mouse events are always attached for hover.
  // When trigger="click" on web (used by the mobile responsive layout) we also
  // need a pressable container — plain View does not fire onPress in RN Web.
  const webMouseProps = Platform.OS === 'web'
    ? {
        // @ts-ignore - Web-specific mouse events
        onMouseEnter: trigger !== 'click' ? handleMouseEnter : undefined,
        onMouseLeave: trigger !== 'click' ? handleMouseLeave : undefined,
      }
    : {};

  return (
    <>
      {trigger === 'click' ? (
        <Pressable
          ref={triggerRef as any}
          style={styles.triggerContainer}
          onPress={Platform.OS !== 'web' ? handleNativePress : handlePress}
          {...(Platform.OS === 'web' ? webMouseProps : {})}
        >
          {children}
        </Pressable>
      ) : (
        <View ref={triggerRef} style={styles.triggerContainer} {...webMouseProps}>
          {children}
        </View>
      )}

      {/* Web tooltip */}
      {Platform.OS === 'web' && isVisible && tooltipContainerRef.current && createPortal && (
        createPortal(
          <>
            {/* Transparent backdrop for click-triggered tooltips — catches outside taps to dismiss */}
            {trigger === 'click' && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 999998,
                  pointerEvents: 'auto',
                }}
                onClick={handleHide}
              />
            )}
            <div
              style={{
                position: 'fixed',
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                pointerEvents: 'auto',
                zIndex: 999999,
                opacity: isTransitioning ? 0.8 : 1,
                transition: 'opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isTransitioning ? 'scale(0.95)' : 'scale(1)',
                willChange: 'opacity, transform',
              }}
              onMouseEnter={trigger !== 'click' ? handleMouseEnter : undefined}
              onMouseLeave={trigger !== 'click' ? handleMouseLeave : undefined}
            >
              {renderTooltipContent()}
            </div>
          </>,
          tooltipContainerRef.current
        )
      )}

      {/* Native tooltip — absolutely positioned above/below the anchor icon */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={isVisible}
          transparent
          animationType="none"
          onRequestClose={() => updateVisibility(false)}
        >
          {/* Full-screen backdrop: tapping anywhere outside the tooltip closes it */}
          <Pressable
            style={StyleSheet.absoluteFillObject}
            onPress={() => updateVisibility(false)}
            accessible={false}
          />

          {/* Tooltip box: absolutely positioned at measured coordinates */}
          <View
            pointerEvents="box-none"
            style={[
              styles.nativeTooltipWrap,
              {
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                maxWidth: Math.min(maxWidth, screenData.width - 24),
                opacity: nativeTooltipSize.width === 0 ? 0 : 1,
              },
            ]}
            onLayout={handleNativeTooltipLayout}
          >
            {renderTooltipContent()}
            <TooltipArrow position={nativeActualPosition} />
          </View>
        </Modal>
      )}
    </>
  );
};

// Convenience wrapper for simple text tooltips.
// Pass trigger="click" explicitly for mobile responsive layouts; omit (defaults
// to "hover") for desktop web. The trigger prop is forwarded as-is so callers
// stay in full control.
export const DLOSimpleTooltip: React.FC<{
  children: React.ReactNode;
  text: string;
  position?: DLOTooltipPosition;
  trigger?: DLOTooltipTrigger;
}> = ({ children, text, position, trigger = 'hover' }) => (
  <DLOTooltip content={text} position={position} trigger={trigger}>
    {children}
  </DLOTooltip>
);

const styles = StyleSheet.create({
  triggerContainer: {
    // No specific styling needed - let children define their own layout
  },
  tooltip: {
    backgroundColor: '#272727',
    borderRadius: 8,
    padding: 16,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    minWidth: 120,
    maxWidth: 320,
  },
  tooltipTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter-Medium',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    marginBottom: 4,
  },
  divider: {
    height: 1,
    backgroundColor: '#404040',
    width: '100%',
    marginVertical: 8,
  },
  tooltipText: {
    color: '#E5E5E5',
    fontFamily: Platform.select({
      ios: 'Inter-Regular',
      android: 'Inter-Regular', 
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
  // Native positioned tooltip wrapper — no centred flexbox
  nativeTooltipWrap: {
    position: 'absolute',
  },
  // Kept for reference but no longer used for click-triggered native tooltips
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    maxWidth: '90%',
    alignItems: 'center',
  },
});

export default DLOTooltip;
